import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AUTH_STUDENT_PUBLIC_ID_KEY } from '../constants/auth-storage.constants';

type ApiRecord = Record<string, unknown>;

export interface AdmissionFormRequest {
  dateOfBirth: string | null;
  gender: string;
  nationality: string;
  curp: string;
  rfc: string;
  maritalStatus: string;
  birthState: string;
  birthCountry: string;
  street: string;
  neighborhood: string;
  municipality: string;
  city: string;
  state: string;
  postalCode: string;
  whatsApp: string;
  alternatePhone: string | null;
  preferredContactMethod: string;
  acceptsEmailNotifications: boolean;
  acceptsWhatsAppNotifications: boolean;
  emergencyContactName: string;
  emergencyContactRelation: string;
  emergencyContactPhone: string;
  previousInstitution: string | null;
  previousDegree: string | null;
  graduationYear: number | null;
  occupation: string | null;
  employer: string | null;
  notes: string | null;
  submit: boolean;
}

export type AdmissionFormResponse = Partial<AdmissionFormRequest>;

export interface StudentProfile {
  publicId: string;
  studentPublicId: string;
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  enrollmentStatus: string;
  enrollmentStatusLabel: string;
  documentsStatus: string;
  documentsStatusLabel: string;
  studyPlanPublicId: string;
  studyPlanName: string;
  currentSemester: number | null;
  enrollmentCode: string;
  registrationNumber: string;
  admissionDate: string | null;
  studentType: string;
  curp: string;
  birthDate: string | null;
  gender: string;
  nationality: string;
  profileImageUrl: string;
}

export interface ExtraordinaryExamRequestSubject {
  subjectPublicId: string;
  subjectCode: string;
  subjectName: string;
  academicRecordPublicId: string;
}

export type ExtraordinaryExamRequestStatus = 'Pending' | 'Accepted' | 'Paid';
export type ExtraordinaryExamPaymentStatus =
  | 'Pending'
  | 'Paid'
  | 'Failed'
  | 'Canceled'
  | 'Expired'
  | null;
export type ExtraordinaryExamReferenceStatus = 'Active' | 'Paid' | 'Expired' | 'Cancelled' | null;

export interface ExtraordinaryExamRequest {
  publicId: string;
  subjectPublicId: string;
  subjectCode: string;
  subjectName: string;
  requestedAt: string;
  status: ExtraordinaryExamRequestStatus;
}

export interface ExtraordinaryExamRequestState {
  isSubmissionOpen: boolean;
  requestLimit: number;
  requestedCount: number;
  remainingCapacity: number;
  requests: ExtraordinaryExamRequest[];
  eligibleSubjects: ExtraordinaryExamRequestSubject[];
}

export interface ProfileImageUploadResponse {
  url: string;
}

export interface StudentPayment {
  publicId: string;
  paymentConceptPublicId: string;
  paymentReferencePublicId: string;
  planPublicId: string;
  reference: string;
  concept: string;
  amount: number;
  method: string;
  status: string;
  dueDate: string | null;
  paidAt: string | null;
}

export interface ExtraordinaryExamRequestPayment {
  requestPublicId: string | null;
  paymentPublicId: string | null;
  paymentStatus: ExtraordinaryExamPaymentStatus;
  paymentConceptPublicId: string | null;
  paymentReferencePublicId: string | null;
  referenceCode: string | null;
  paymentReferenceStatus: ExtraordinaryExamReferenceStatus;
}

export interface CheckoutSessionRequest {
  paymentPublicId?: string;
  paymentConceptPublicId?: string;
  paymentReferencePublicId?: string;
  planPublicId?: string;
  successUrl?: string;
  cancelUrl?: string;
}

export interface PaymentStatusDetails {
  paymentId: string;
  reference: string;
  concept: string;
  amount: number;
  currency: string;
  status: string;
  paymentMethod: string;
  paidAt: string | null;
  createdAt: string | null;
  receiptUrl: string;
}

export interface PaymentHistoryInstallment {
  number: number;
  dueDate: string | null;
  amount: number;
  status: string;
  paymentPublicId: string;
  paymentConceptPublicId: string;
  paymentReferencePublicId: string;
  paidAt: string | null;
}

export interface PaymentHistoryPlan {
  planPublicId: string;
  paymentConceptPublicId: string;
  planTag: string;
  conceptDescription: string;
  totalAmount: number;
  totalInstallments: number;
  status: string;
  installments: PaymentHistoryInstallment[];
}

export interface PaymentHistoryPeriod {
  periodPublicId: string;
  periodLabel: string;
  year: number | null;
  plans: PaymentHistoryPlan[];
}

export interface StandalonePayment {
  publicId: string;
  conceptCode: string;
  conceptDescription: string;
  amountPaid: number;
  status: string;
  paymentMethod: string;
  paymentDate: string | null;
}

export interface StudentPaymentHistory {
  studentName: string;
  registrationNumber: string;
  periods: PaymentHistoryPeriod[];
  standalonePayments: StandalonePayment[];
}

export interface StudentPaymentPlanInstallment {
  publicId: string;
  installmentNumber: number;
  dueDate: string | null;
  amount: number;
  status: string;
  paymentReferencePublicId: string;
}

export interface StudentPaymentPlan {
  publicId: string;
  studentName: string;
  conceptDescription: string;
  paymentConceptPublicId: string;
  totalAmount: number;
  totalInstallments: number;
  startDate: string | null;
  status: string;
  notes: string;
  periodPublicId: string;
  periodName: string;
  installments: StudentPaymentPlanInstallment[];
}

export interface StudentEnrollment {
  publicId: string;
  studentPublicId: string;
  studyPlanPublicId: string;
  folio: string;
  program: string;
  status: string;
  requestedAt: string | null;
  amount: number | null;
}

export enum StudentDocumentStatus {
  Pending = 'pending',
  Submitted = 'submitted',
  Verified = 'verified',
  Rejected = 'rejected',
}

export interface StudentDocument {
  id: string;
  documentTypePublicId: string;
  name: string;
  status: StudentDocumentStatus;
  fileName: string;
  fileUrl: string;
  rejectionReason: string;
}

export interface ExtraActivityOffering {
  publicId: string;
  name: string;
  type: string;
  modality: string;
  startDate: string | null;
  cost: number | null;
  totalHours: number | null;
}

export enum ExtraEnrollmentStatus {
  Completed = 'completed',
  InProgress = 'inProgress',
  Pending = 'pending',
}

export interface ExtraEnrollmentCourse {
  publicId: string;
  name: string;
  type: 'course' | 'diploma' | 'workshop';
  startDate: string | null;
  endDate: string | null;
  status: ExtraEnrollmentStatus;
  grade: number | null;
  certificateUrl: string;
}

export enum AcademicSubjectStatus {
  Approved = 'approved',
  InProgress = 'inProgress',
  Failed = 'failed',
}

export interface AcademicSubject {
  publicId: string;
  code: string;
  name: string;
  grade: string;
  status: AcademicSubjectStatus;
  gradeSheetFolio: string | null;
  cycleNumber: number;
  period: string;
  credits: number | null;
  auditedAt: string | null;
}

export interface AcademicSemester {
  title: string;
  status: string;
  subjects: AcademicSubject[];
}

export interface StudentScheduleSlot {
  publicId: string;
  dayOfWeek: string;
  startTime: string;
  endTime: string;
  classroom: string | null;
}

export interface StudentScheduleSubject {
  academicRecordPublicId: string;
  groupPublicId: string;
  subjectName: string;
  groupName: string;
  periodName: string;
  periodStartDate: string;
  periodEndDate: string;
  professorFullName: string;
  schedules: StudentScheduleSlot[];
}

export interface StudentSchedule {
  studentPublicId: string;
  subjects: StudentScheduleSubject[];
}

export interface StudentKardex {
  folio: string;
  studentName: string;
  enrollmentCode: string;
  program: string;
  plan: string;
  status: string;
  average: string;
  approvedSubjects: number;
  approvedCredits: number;
  totalCredits: number;
  semesters: AcademicSemester[];
}

export interface GradePartial {
  publicId: string;
  name: string;
  type: string;
  score: number | null;
  weight: number | null;
  date: string | null;
}

/** A read-only academic record returned for the authenticated student. */
export interface StudentAcademicRecord {
  publicId: string;
  periodPublicId: string;
  subjectName: string;
  groupCohortName: string;
  periodName: string;
  professorFullName: string;
  grade: string | null;
  status: string;
  gradePartials: GradePartial[];
}

export type GradeCorrectionRequestState =
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'registered';

export interface GradeCorrectionAuditEntry {
  state: GradeCorrectionRequestState;
  actorName: string;
  occurredAt: string;
  note: string;
}

export interface GradeCorrectionRequest {
  publicId: string;
  academicRecordPublicId: string;
  subjectName: string;
  periodName: string;
  studentName?: string;
  registrationNumber?: string;
  originalGrade: string;
  rectifiedGrade?: string | null;
  reason: string;
  evidence: string;
  state: GradeCorrectionRequestState;
  submittedAt: string;
  updatedAt?: string | null;
  resolution?: string | null;
  auditHistory?: GradeCorrectionAuditEntry[];
}

export interface CreateGradeCorrectionRequest {
  academicRecordPublicId: string;
  reason: string;
  evidence: string;
}

export interface GradeCorrectionDecision {
  decision: 'approved' | 'rejected';
  rectifiedGrade?: string;
  resolution: string;
  evidence: string;
}

export interface StudentPermissions {
  userType: string;
  roles: string[];
  permissions: string[];
}

export interface PublicStudyPlan {
  publicId: string;
  name: string;
  degreeLevel: string;
}

export interface MoodleAccount {
  publicId: string;
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  email: string;
  cohort: string | null;
  phone: string | null;
  curp: string;
}

export type MoodleAccountResult =
  | { status: 'available'; account: MoodleAccount }
  | { status: 'not-available' };

@Injectable({ providedIn: 'root' })
export class StudentPortalService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  getProfile() {
    return this.http
      .get<unknown>(`${this.apiUrl}/user/profile`)
      .pipe(map((res) => this.toProfile(res)));
  }

  getCurrentStudentProfile() {
    return this.getProfile().pipe(
      switchMap((profile) =>
        this.getStudentProfileMe().pipe(
          map((studentProfile) => this.mergeProfiles(profile, studentProfile)),
          tap((mergedProfile) => {
            if (mergedProfile.studentPublicId) {
              // Sourced from the authoritative /students/me response. Nothing reads this
              // key today, but it stays correct and is the right place to seed it if a
              // reader returns.
              localStorage.setItem(AUTH_STUDENT_PUBLIC_ID_KEY, mergedProfile.studentPublicId);
            }
          }),
          catchError(() => of(profile)),
        ),
      ),
    );
  }

  getStudentProfileMe() {
    return this.http
      .get<unknown>(`${this.apiUrl}/students/me`)
      .pipe(map((res) => this.toProfile(res)));
  }

  getStudentProfileMeRaw() {
    return this.http.get<unknown>(`${this.apiUrl}/students/me`);
  }

  getMyExtraordinaryExamRequests() {
    return this.http.get<ExtraordinaryExamRequestState>(
      `${this.apiUrl}/students/me/extraordinary-requests`,
    );
  }

  getMyExtraordinaryExamRequestPayments() {
    return this.http
      .get<unknown>(`${this.apiUrl}/students/me/extraordinary-requests/payments`)
      .pipe(
        map((res) => this.toArray(res).map((item) => this.toExtraordinaryExamRequestPayment(item))),
      );
  }

  submitMyExtraordinaryExamRequest(subjectPublicId: string) {
    return this.http.post(`${this.apiUrl}/students/me/extraordinary-requests`, {
      subjectPublicId,
    });
  }

  createExtraordinaryRequestCheckout(
    paymentConceptPublicId: string,
    paymentReferencePublicId: string,
  ) {
    return this.http.post<unknown>(`${this.apiUrl}/payments/checkout-session`, {
      paymentConceptPublicId,
      paymentReferencePublicId,
    });
  }

  getMyStudentDocuments() {
    return this.http
      .get<unknown>(`${this.apiUrl}/students/me/documents`)
      .pipe(
        map((res) => this.toArray(res).map((item, index) => this.toStudentDocument(item, index))),
      );
  }

  uploadMyStudentDocument(documentTypePublicId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentTypePublicId', documentTypePublicId);

    return this.http
      .post<unknown>(`${this.apiUrl}/students/me/documents`, formData)
      .pipe(map((res) => this.toStudentDocument(res, 0)));
  }

  uploadProfileImage(file: File) {
    const formData = new FormData();
    formData.append('image', file);

    return this.http
      .put<unknown>(`${this.apiUrl}/user/profile-image`, formData)
      .pipe(map((res) => this.toProfileImageUploadResponse(res)));
  }

  saveMyAdmissionForm(payload: AdmissionFormRequest) {
    return this.http.put<unknown>(`${this.apiUrl}/students/me/admission-form`, payload);
  }

  getMyAdmissionForm() {
    return this.http
      .get<unknown>(`${this.apiUrl}/students/me/admission-form`)
      .pipe(map((res) => this.toAdmissionFormResponse(res)));
  }

  getPublicStudyPlans() {
    return this.http
      .get<unknown>(`${this.apiUrl}/study-plans/public`)
      .pipe(map((res) => this.toArray(res).map((item) => this.toPublicStudyPlan(item))));
  }

  getStudyPlanName(studyPlanId: string) {
    return this.getPublicStudyPlans().pipe(
      map((plans) => plans.find((plan) => plan.publicId === studyPlanId)?.name ?? ''),
    );
  }

  getPayments() {
    return this.http
      .get<unknown>(`${this.apiUrl}/payments/me`)
      .pipe(map((res) => this.toArray(res).map((item, index) => this.toPayment(item, index))));
  }

  getPaymentHistory() {
    return this.http
      .get<unknown>(`${this.apiUrl}/students/me/payment-history`)
      .pipe(map((res) => this.toPaymentHistory(res)));
  }

  getMyPaymentPlans() {
    return this.http
      .get<unknown>(`${this.apiUrl}/students/me/payment-plans`)
      .pipe(map((res) => this.toPaymentPlans(res)));
  }

  createCheckoutSession(payload: CheckoutSessionRequest) {
    return this.http.post<unknown>(`${this.apiUrl}/payments/checkout-session`, payload);
  }

  getPaymentStatus(paymentId: string) {
    return this.http
      .get<unknown>(`${this.apiUrl}/payments/${paymentId}/status`)
      .pipe(map((res) => this.toPaymentStatusDetails(res, paymentId)));
  }

  requestPaymentReference(paymentConceptPublicId: string) {
    return this.http.post<unknown>(`${this.apiUrl}/payments/me/request-reference`, {
      paymentConceptPublicId,
    });
  }

  getMyPaymentDetail(paymentId: string) {
    return this.http
      .get<unknown>(`${this.apiUrl}/payments/me/${paymentId}`)
      .pipe(map((res) => this.toPayment(res, 0)));
  }

  getMyPaymentReceipt(paymentId: string) {
    return this.http.get<unknown>(`${this.apiUrl}/payments/me/${paymentId}/receipt`);
  }

  getPaymentReceiptPdf(paymentId: string) {
    return this.http.get(`${this.apiUrl}/payments/me/${paymentId}/receipt/pdf`, {
      responseType: 'blob',
    });
  }

  getAccountStatementPdf() {
    return this.http.get(`${this.apiUrl}/students/me/account-statement/pdf`, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  getEnrollments() {
    return this.http
      .get<unknown>(`${this.apiUrl}/academic-enrollments/me`)
      .pipe(map((res) => this.toArray(res).map((item, index) => this.toEnrollment(item, index))));
  }

  getUnreadNotificationCount() {
    return this.http
      .get<unknown>(`${this.apiUrl}/notifications/me/unread-count`)
      .pipe(map((res) => this.toCount(res)));
  }

  getExtraActivities() {
    return this.http
      .get<unknown>(`${this.apiUrl}/extra-activities?page=1&pageSize=10`)
      .pipe(
        map((res) => this.toArray(res).map((item, index) => this.toExtraActivity(item, index))),
      );
  }

  getExtraEnrollmentsByStudent(studentId: string) {
    return this.http
      .get<unknown>(`${this.apiUrl}/extra-enrollments/by-student/${studentId}`)
      .pipe(
        map((res) => this.toArray(res).map((item, index) => this.toExtraEnrollment(item, index))),
      );
  }

  getKardex(studentId: string) {
    return this.http
      .get<unknown>(`${this.apiUrl}/students/${studentId}/kardex`)
      .pipe(map((res) => this.toKardex(res)));
  }

  getKardexPdf(studentId: string) {
    return this.http.get(`${this.apiUrl}/students/${studentId}/kardex/pdf`, {
      responseType: 'blob',
    });
  }

  getMyAcademicRecords() {
    return this.http
      .get<unknown>(`${this.apiUrl}/academic-records/me`)
      .pipe(
        map((res) =>
          this.toArray(res).map((item, index) => this.toStudentAcademicRecord(item, index)),
        ),
      );
  }

  getMyAcademicRecordsByPeriod(periodPublicId: string) {
    return this.http
      .get<unknown>(
        `${this.apiUrl}/academic-records/me/by-period/${encodeURIComponent(periodPublicId)}`,
      )
      .pipe(
        map((res) =>
          this.toArray(res).map((item, index) => this.toStudentAcademicRecord(item, index)),
        ),
      );
  }

  createGradeCorrectionRequest(payload: CreateGradeCorrectionRequest) {
    return this.http.post<GradeCorrectionRequest>(
      `${this.apiUrl}/grade-correction-requests`,
      payload,
    );
  }

  getMyGradeCorrectionRequests() {
    return this.http
      .get<GradeCorrectionRequest[]>(`${this.apiUrl}/grade-correction-requests/me`)
      .pipe(map((requests) => this.normalizeGradeCorrectionRequests(requests)));
  }

  getGradeCorrectionRequests() {
    return this.http
      .get<GradeCorrectionRequest[]>(`${this.apiUrl}/grade-correction-requests`)
      .pipe(map((requests) => this.normalizeGradeCorrectionRequests(requests)));
  }

  decideGradeCorrectionRequest(publicId: string, payload: GradeCorrectionDecision) {
    return this.http.patch<GradeCorrectionRequest>(
      `${this.apiUrl}/grade-correction-requests/${encodeURIComponent(publicId)}/decision`,
      payload,
    );
  }

  getMySchedule() {
    return this.http.get<StudentSchedule>(`${this.apiUrl}/students/me/schedule`);
  }

  getMyScheduleWithServerDate(): Observable<{
    schedule: StudentSchedule;
    serverDate: Date | null;
  }> {
    return this.http
      .get<StudentSchedule>(`${this.apiUrl}/students/me/schedule`, { observe: 'response' })
      .pipe(
        map((response) => ({
          schedule: response.body as StudentSchedule,
          serverDate: this.parseServerDate(response.headers.get('date')),
        })),
      );
  }

  getGradePartials(academicRecordId: string) {
    return this.http
      .get<unknown>(`${this.apiUrl}/grade-partials/by-academic-record/${academicRecordId}`)
      .pipe(map((res) => this.toArray(res).map((item, index) => this.toGradePartial(item, index))));
  }

  /**
   * A student's `permissions` array is normally empty by design — student
   * access is identity-based, not permission-based — so an empty array is a
   * valid, non-error response and must not be treated as a failure.
   */
  getMyPermissions() {
    return this.http
      .get<unknown>(`${this.apiUrl}/me/permissions`)
      .pipe(map((res) => this.toPermissions(res)));
  }

  getMoodleAccount(): Observable<MoodleAccountResult> {
    return this.http.get<unknown>(`${this.apiUrl}/students/me/moodle-account`).pipe(
      map(
        (res): MoodleAccountResult => ({ status: 'available', account: this.toMoodleAccount(res) }),
      ),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of<MoodleAccountResult>({ status: 'not-available' });
        }

        return throwError(() => error);
      }),
    );
  }

  private parseServerDate(value: string | null): Date | null {
    const timestamp = value ? Date.parse(value) : Number.NaN;
    return Number.isFinite(timestamp) ? new Date(timestamp) : null;
  }

  private toProfile(value: unknown): StudentProfile {
    const rootRecord = this.isRecord(value) ? value : {};
    const record = this.unwrapRecord(value);
    const firstName = this.stringValue(record, ['firstName', 'name', 'nombre']);
    const lastName = this.stringValue(record, ['lastName', 'lastNames', 'apellido']);
    const fullName =
      this.stringValue(record, ['fullName', 'userName', 'displayName']) ||
      [firstName, lastName].filter(Boolean).join(' ');

    return {
      publicId: this.stringValue(record, ['publicId', 'id']),
      studentPublicId: this.studentPublicId(record) || this.studentPublicId(rootRecord),
      fullName: fullName || 'Alumno',
      firstName,
      lastName,
      email: this.stringValue(record, ['email']),
      phone: this.stringValue(record, ['phone', 'telefono']),
      role: this.stringValue(record, ['role']) || 'Student',
      status:
        this.stringValue(record, ['status', 'userStatus', 'studentStatus', 'estatus']) ||
        this.stringValue(rootRecord, ['status', 'userStatus', 'studentStatus', 'estatus']),
      enrollmentStatus: this.stringValue(record, ['enrollmentStatus']),
      enrollmentStatusLabel: this.stringValue(record, ['enrollmentStatusLabel']),
      documentsStatus: this.stringValue(record, ['documentsStatus']),
      documentsStatusLabel: this.stringValue(record, ['documentsStatusLabel']),
      studyPlanPublicId: this.studyPlanPublicId(record) || this.studyPlanPublicId(rootRecord),
      studyPlanName: this.studyPlanName(record) || this.studyPlanName(rootRecord),
      currentSemester: this.numberValue(record, ['currentSemester', 'semester']),
      enrollmentCode: this.stringValue(record, [
        'enrollmentCode',
        'registrationNumber',
        'matricula',
        'studentNumber',
      ]),
      registrationNumber: this.stringValue(record, ['registrationNumber']),
      admissionDate: this.dateValue(record, ['admissionDate']),
      studentType: this.stringValue(record, ['studentType']),
      curp: this.stringValue(record, ['curp', 'CURP']),
      birthDate: this.dateValue(record, ['birthDate', 'dateOfBirth', 'fechaNacimiento']),
      gender: this.stringValue(record, ['gender', 'genero']),
      nationality: this.stringValue(record, ['nationality', 'nacionalidad']),
      profileImageUrl: this.profileImageUrl(record),
    };
  }

  private mergeProfiles(
    baseProfile: StudentProfile,
    studentProfile: StudentProfile,
  ): StudentProfile {
    return {
      publicId: this.preferString(studentProfile.publicId, baseProfile.publicId),
      studentPublicId: this.preferString(
        studentProfile.studentPublicId,
        baseProfile.studentPublicId,
      ),
      fullName: this.preferString(studentProfile.fullName, baseProfile.fullName),
      firstName: this.preferString(studentProfile.firstName, baseProfile.firstName),
      lastName: this.preferString(studentProfile.lastName, baseProfile.lastName),
      email: this.preferString(studentProfile.email, baseProfile.email),
      phone: this.preferString(studentProfile.phone, baseProfile.phone),
      role: this.preferString(studentProfile.role, baseProfile.role),
      status: this.preferString(studentProfile.status, baseProfile.status),
      enrollmentStatus: this.preferString(
        studentProfile.enrollmentStatus,
        baseProfile.enrollmentStatus,
      ),
      enrollmentStatusLabel: this.preferString(
        studentProfile.enrollmentStatusLabel,
        baseProfile.enrollmentStatusLabel,
      ),
      documentsStatus: this.preferString(
        studentProfile.documentsStatus,
        baseProfile.documentsStatus,
      ),
      documentsStatusLabel: this.preferString(
        studentProfile.documentsStatusLabel,
        baseProfile.documentsStatusLabel,
      ),
      studyPlanPublicId: this.preferString(
        studentProfile.studyPlanPublicId,
        baseProfile.studyPlanPublicId,
      ),
      studyPlanName: this.preferString(studentProfile.studyPlanName, baseProfile.studyPlanName),
      currentSemester: studentProfile.currentSemester ?? baseProfile.currentSemester,
      enrollmentCode: this.preferString(studentProfile.enrollmentCode, baseProfile.enrollmentCode),
      registrationNumber: this.preferString(
        studentProfile.registrationNumber,
        baseProfile.registrationNumber,
      ),
      admissionDate: studentProfile.admissionDate || baseProfile.admissionDate,
      studentType: this.preferString(studentProfile.studentType, baseProfile.studentType),
      curp: this.preferString(studentProfile.curp, baseProfile.curp),
      birthDate: studentProfile.birthDate || baseProfile.birthDate,
      gender: this.preferString(studentProfile.gender, baseProfile.gender),
      nationality: this.preferString(studentProfile.nationality, baseProfile.nationality),
      profileImageUrl: this.preferString(
        studentProfile.profileImageUrl,
        baseProfile.profileImageUrl,
      ),
    };
  }

  private preferString(primary: string, fallback: string): string {
    return primary.trim() || fallback.trim();
  }

  private toProfileImageUploadResponse(value: unknown): ProfileImageUploadResponse {
    const record = this.unwrapRecord(value);
    const url =
      this.stringValue(record, [
        'url',
        'profileImageUrl',
        'profileImage',
        'imageUrl',
        'fileUrl',
        'signedUrl',
        'presignedUrl',
      ]) || this.fileUrlFromNested(record, new Set<ApiRecord>());

    return { url: this.toResourceUrl(url) };
  }

  private profileImageUrl(record: ApiRecord): string {
    const userRecord = this.recordValue(record, ['user', 'profile']);
    const url =
      this.stringValue(record, [
        'profileImageUrl',
        'profileImage',
        'avatarUrl',
        'photoUrl',
        'imageUrl',
        'picture',
      ]) ||
      this.stringValue(userRecord, [
        'profileImageUrl',
        'profileImage',
        'avatarUrl',
        'photoUrl',
        'imageUrl',
        'picture',
      ]);

    return this.toResourceUrl(url);
  }

  private toPayment(value: unknown, index: number): StudentPayment {
    const record = this.unwrapRecord(value);
    const conceptRecord = this.recordValue(record, ['paymentConcept', 'concept']);
    const referenceRecord = this.recordValue(record, ['paymentReference', 'reference']);
    const referenceConceptRecord = this.recordValue(referenceRecord, ['paymentConcept', 'concept']);

    return {
      publicId: this.stringValue(record, ['publicId', 'id']) || String(index),
      paymentConceptPublicId:
        this.stringValue(record, ['paymentConceptPublicId', 'conceptPublicId']) ||
        this.stringValue(conceptRecord, ['publicId', 'id']),
      paymentReferencePublicId:
        this.stringValue(record, ['paymentReferencePublicId', 'referencePublicId']) ||
        this.stringValue(referenceRecord, ['publicId', 'id']),
      planPublicId:
        this.stringValue(record, ['planPublicId', 'paymentPlanPublicId']) ||
        this.stringValue(this.recordValue(record, ['plan', 'paymentPlan']), ['publicId', 'id']),
      reference:
        this.stringValue(record, ['reference', 'folio', 'code', 'paymentCode']) ||
        this.stringValue(referenceRecord, ['reference', 'folio', 'code', 'paymentCode']) ||
        'Sin referencia',
      concept:
        this.paymentConceptName(record) ||
        this.paymentConceptName(conceptRecord) ||
        this.paymentConceptName(referenceRecord) ||
        this.paymentConceptName(referenceConceptRecord) ||
        'Pago',
      amount:
        this.numberValue(record, [
          'amount',
          'totalAmount',
          'paidAmount',
          'amountPaid',
          'amountDue',
          'balance',
          'price',
          'cost',
        ]) ??
        this.numberValue(referenceRecord, [
          'amount',
          'totalAmount',
          'paidAmount',
          'amountPaid',
          'amountDue',
          'balance',
        ]) ??
        this.numberValue(conceptRecord, ['amount', 'totalAmount', 'price', 'cost']) ??
        0,
      method: this.stringValue(record, ['method', 'paymentMethod', 'paymentType', 'type']),
      status: this.stringValue(record, ['status']),
      dueDate: this.dateValue(record, ['dueDate', 'expiresAt', 'deadline']),
      paidAt: this.dateValue(record, ['paidAt', 'paymentDate']),
    };
  }

  private toExtraordinaryExamRequestPayment(value: unknown): ExtraordinaryExamRequestPayment {
    const record = this.unwrapRecord(value);
    const text = (key: string) => this.stringValue(record, [key]) || null;

    return {
      requestPublicId: text('requestPublicId'),
      paymentPublicId: text('paymentPublicId'),
      paymentStatus: text('paymentStatus') as ExtraordinaryExamPaymentStatus,
      paymentConceptPublicId: text('paymentConceptPublicId'),
      paymentReferencePublicId: text('paymentReferencePublicId'),
      referenceCode: text('referenceCode'),
      paymentReferenceStatus: text('paymentReferenceStatus') as ExtraordinaryExamReferenceStatus,
    };
  }

  private toPaymentHistory(value: unknown): StudentPaymentHistory {
    const record = this.unwrapRecord(value);

    return {
      studentName: this.stringValue(record, ['studentName', 'fullName', 'name']),
      registrationNumber: this.stringValue(record, ['registrationNumber', 'enrollmentCode']),
      periods: this.arrayValue(record, ['periods']).map((period) =>
        this.toPaymentHistoryPeriod(period),
      ),
      standalonePayments: this.arrayValue(record, ['standalonePayments', 'payments']).map(
        (payment) => this.toStandalonePayment(payment),
      ),
    };
  }

  private toPaymentHistoryPeriod(value: unknown): PaymentHistoryPeriod {
    const record = this.unwrapRecord(value);

    return {
      periodPublicId: this.stringValue(record, ['periodPublicId', 'publicId', 'id']),
      periodLabel: this.stringValue(record, ['periodLabel', 'label', 'name']) || 'Periodo',
      year: this.numberValue(record, ['year']),
      plans: this.arrayValue(record, ['plans', 'paymentPlans']).map((plan) =>
        this.toPaymentHistoryPlan(plan),
      ),
    };
  }

  private toPaymentHistoryPlan(value: unknown): PaymentHistoryPlan {
    const record = this.unwrapRecord(value);

    return {
      planPublicId: this.stringValue(record, ['planPublicId', 'publicId', 'id']),
      paymentConceptPublicId:
        this.stringValue(record, ['paymentConceptPublicId', 'conceptPublicId']) ||
        this.stringValue(this.recordValue(record, ['paymentConcept', 'concept']), [
          'publicId',
          'id',
        ]),
      planTag: this.stringValue(record, ['planTag', 'tag', 'type']),
      conceptDescription:
        this.stringValue(record, ['conceptDescription', 'description', 'conceptName']) || 'Plan',
      totalAmount: this.numberValue(record, ['totalAmount', 'amount']) ?? 0,
      totalInstallments: this.numberValue(record, ['totalInstallments', 'installmentsCount']) ?? 0,
      status: this.stringValue(record, ['status']) || 'Active',
      installments: this.arrayValue(record, ['installments', 'fees']).map((installment) =>
        this.toPaymentHistoryInstallment(installment),
      ),
    };
  }

  private toPaymentHistoryInstallment(value: unknown): PaymentHistoryInstallment {
    const record = this.unwrapRecord(value);

    return {
      number: this.numberValue(record, ['number', 'installmentNumber']) ?? 0,
      dueDate: this.dateValue(record, ['dueDate', 'expiresAt']),
      amount: this.numberValue(record, ['amount', 'totalAmount']) ?? 0,
      status: this.stringValue(record, ['status']) || 'Pending',
      paymentPublicId: this.stringValue(record, ['paymentPublicId', 'paymentId']),
      paymentConceptPublicId:
        this.stringValue(record, ['paymentConceptPublicId', 'conceptPublicId']) ||
        this.stringValue(this.recordValue(record, ['paymentConcept', 'concept']), [
          'publicId',
          'id',
        ]),
      paymentReferencePublicId:
        this.stringValue(record, ['paymentReferencePublicId', 'referencePublicId']) ||
        this.stringValue(this.recordValue(record, ['paymentReference', 'reference']), [
          'publicId',
          'id',
        ]),
      paidAt: this.dateValue(record, ['paidAt', 'paymentDate']),
    };
  }

  private toPaymentPlans(value: unknown): StudentPaymentPlan[] {
    const record = this.unwrapRecord(value);
    const plans = Array.isArray(value)
      ? value
      : this.arrayValue(record, ['paymentPlans', 'plans', 'items', 'data', 'results']);

    return plans.map((plan) => this.toPaymentPlan(plan));
  }

  private toPaymentPlan(value: unknown): StudentPaymentPlan {
    const record = this.unwrapRecord(value);

    return {
      publicId: this.stringValue(record, ['publicId', 'id', 'planPublicId']),
      studentName: this.stringValue(record, ['studentName']),
      conceptDescription:
        this.stringValue(record, ['conceptDescription', 'description', 'conceptName']) || 'Plan',
      paymentConceptPublicId:
        this.stringValue(record, ['paymentConceptPublicId', 'conceptPublicId']) ||
        this.stringValue(this.recordValue(record, ['paymentConcept', 'concept']), [
          'publicId',
          'id',
        ]),
      totalAmount: this.numberValue(record, ['totalAmount', 'amount']) ?? 0,
      totalInstallments: this.numberValue(record, ['totalInstallments', 'installmentsCount']) ?? 0,
      startDate: this.dateValue(record, ['startDate']),
      status: this.stringValue(record, ['status']) || 'Active',
      notes: this.stringValue(record, ['notes']),
      periodPublicId: this.stringValue(record, ['periodPublicId']),
      periodName: this.stringValue(record, ['periodName', 'periodLabel']) || 'Periodo',
      installments: this.arrayValue(record, [
        'installments',
        'paymentPlanInstallments',
        'fees',
      ]).map((installment) => this.toPaymentPlanInstallment(installment)),
    };
  }

  private toPaymentPlanInstallment(value: unknown): StudentPaymentPlanInstallment {
    const record = this.unwrapRecord(value);

    return {
      publicId: this.stringValue(record, ['publicId', 'id']),
      installmentNumber: this.numberValue(record, ['installmentNumber', 'number']) ?? 0,
      dueDate: this.dateValue(record, ['dueDate', 'expiresAt']),
      amount: this.numberValue(record, ['amount', 'totalAmount']) ?? 0,
      status: this.stringValue(record, ['status']) || 'Pending',
      paymentReferencePublicId:
        this.stringValue(record, ['paymentReferencePublicId', 'referencePublicId']) ||
        this.stringValue(this.recordValue(record, ['paymentReference', 'reference']), [
          'publicId',
          'id',
        ]),
    };
  }

  private toStandalonePayment(value: unknown): StandalonePayment {
    const record = this.unwrapRecord(value);

    return {
      publicId: this.stringValue(record, ['publicId', 'id']),
      conceptCode: this.stringValue(record, ['conceptCode', 'code']),
      conceptDescription:
        this.stringValue(record, ['conceptDescription', 'description', 'conceptName']) || 'Pago',
      amountPaid: this.numberValue(record, ['amountPaid', 'amount', 'paidAmount']) ?? 0,
      status: this.stringValue(record, ['status']) || 'Completed',
      paymentMethod: this.stringValue(record, ['paymentMethod', 'method']),
      paymentDate: this.dateValue(record, ['paymentDate', 'paidAt', 'createdAt']),
    };
  }

  private toPaymentStatusDetails(value: unknown, fallbackPaymentId: string): PaymentStatusDetails {
    const record = this.unwrapRecord(value);
    const paymentRecord = this.recordValue(record, ['payment', 'paymentDetail', 'detail']);
    const referenceRecord = this.recordValue(record, ['paymentReference', 'reference']);
    const conceptRecord = this.recordValue(record, ['paymentConcept', 'concept']);
    const source = Object.keys(paymentRecord).length > 0 ? paymentRecord : record;

    return {
      paymentId:
        this.stringValue(source, ['paymentId', 'paymentPublicId', 'publicId', 'id']) ||
        this.stringValue(record, ['paymentId', 'paymentPublicId', 'publicId', 'id']) ||
        fallbackPaymentId,
      reference:
        this.stringValue(source, ['reference', 'folio', 'code', 'paymentCode']) ||
        this.stringValue(referenceRecord, ['reference', 'folio', 'code', 'paymentCode']) ||
        'Sin referencia',
      concept:
        this.paymentConceptName(source) ||
        this.paymentConceptName(conceptRecord) ||
        this.paymentConceptName(referenceRecord) ||
        'Pago',
      amount:
        this.numberValue(source, [
          'amount',
          'totalAmount',
          'paidAmount',
          'amountPaid',
          'amountDue',
          'balance',
          'price',
          'cost',
        ]) ??
        this.numberValue(referenceRecord, ['amount', 'totalAmount', 'amountPaid', 'amountDue']) ??
        this.numberValue(conceptRecord, ['amount', 'totalAmount', 'price', 'cost']) ??
        0,
      currency: this.stringValue(source, ['currency', 'currencyCode']) || 'MXN',
      status: this.stringValue(source, ['status', 'paymentStatus']) || 'success',
      paymentMethod: this.stringValue(source, ['method', 'paymentMethod', 'paymentType', 'type']),
      paidAt: this.dateValue(source, ['paidAt', 'paymentDate', 'paidDate', 'updatedAt']),
      createdAt: this.dateValue(source, ['createdAt', 'createdDate', 'date']),
      receiptUrl: this.stringValue(source, ['receiptUrl', 'receipt', 'receiptPdfUrl', 'pdfUrl']),
    };
  }

  private paymentConceptName(record: ApiRecord): string {
    const direct = this.stringValue(record, [
      'concept',
      'conceptName',
      'conceptDescription',
      'description',
      'name',
      'title',
      'label',
      'paymentConceptName',
      'paymentConceptDescription',
      'paymentConceptIdentifier',
      'paymentDescription',
      'paymentName',
      'serviceName',
      'itemName',
    ]);

    if (direct) {
      return direct;
    }

    return (
      [
        this.recordValue(record, ['paymentConcept', 'concept']),
        this.recordValue(record, ['paymentReference', 'reference']),
        this.recordValue(record, ['payment', 'paymentDetail']),
        this.recordValue(record, ['plan', 'paymentPlan']),
      ]
        .map((nestedRecord) =>
          this.stringValue(nestedRecord, [
            'concept',
            'conceptName',
            'conceptDescription',
            'description',
            'name',
            'title',
            'label',
          ]),
        )
        .find(Boolean) || ''
    );
  }

  private toAdmissionFormResponse(value: unknown): AdmissionFormResponse {
    const record = this.unwrapRecord(value);
    const admissionForm = this.recordValue(record, ['admissionForm', 'form']);

    return Object.keys(admissionForm).length > 0
      ? (admissionForm as AdmissionFormResponse)
      : (record as AdmissionFormResponse);
  }

  private toEnrollment(value: unknown, index: number): StudentEnrollment {
    const record = this.unwrapRecord(value);

    return {
      publicId: this.stringValue(record, ['publicId', 'id']) || String(index),
      studentPublicId: this.studentPublicId(record),
      studyPlanPublicId: this.studyPlanPublicId(record),
      folio: this.stringValue(record, ['folio', 'reference']) || `INS-${index + 1}`,
      program:
        this.studyPlanName(record) ||
        this.stringValue(this.recordValue(record, ['studyPlan', 'program', 'period']), ['name']) ||
        'Programa académico',
      status: this.stringValue(record, ['status']) || 'pending',
      requestedAt: this.dateValue(record, ['requestedAt', 'createdAt', 'date']),
      amount: this.numberValue(record, ['amount', 'totalAmount']),
    };
  }

  private toStudentDocument(value: unknown, index: number): StudentDocument {
    const record = this.unwrapRecord(value);
    const documentType = this.recordValue(record, ['documentType', 'type', 'requirement']);
    const fileRecord = this.recordValue(record, ['file', 'documentFile', 'attachment', 'resource']);
    const rawStatus = this.stringValue(record, ['status', 'documentStatus', 'verificationStatus']);
    const fileName =
      this.stringValue(record, ['fileName', 'filename', 'originalFileName']) ||
      this.stringValue(fileRecord, ['fileName', 'filename', 'originalFileName', 'name']);
    const fileUrl = this.toResourceUrl(this.studentDocumentFileUrl(record, fileRecord));

    return {
      id:
        this.stringValue(record, ['publicId', 'id', 'studentDocumentPublicId']) ||
        this.stringValue(documentType, ['publicId', 'id']) ||
        `DOC-${index + 1}`,
      documentTypePublicId:
        this.stringValue(record, ['documentTypePublicId', 'documentTypeId']) ||
        this.stringValue(documentType, ['publicId', 'id']),
      name:
        this.stringValue(record, ['name', 'documentName', 'documentTypeName', 'title']) ||
        this.stringValue(documentType, ['name', 'title', 'description']) ||
        `Documento ${index + 1}`,
      status: this.toStudentDocumentStatus(rawStatus, record, Boolean(fileName || fileUrl)),
      fileName,
      fileUrl,
      rejectionReason: this.stringValue(record, [
        'rejectionReason',
        'rejectReason',
        'rejectedReason',
        'rejectionMessage',
        'rejectMessage',
        'reviewMessage',
        'reviewComments',
        'reviewNotes',
        'comments',
        'comment',
        'notes',
        'note',
        'observations',
        'observation',
        'observaciones',
      ]),
    };
  }

  private toStudentDocumentStatus(
    status: string,
    record: ApiRecord,
    hasUploadedFile = false,
  ): StudentDocument['status'] {
    const normalizedStatus = this.normalizeToken(status);

    if (
      normalizedStatus.includes('approved') ||
      normalizedStatus.includes('verified') ||
      normalizedStatus.includes('validado') ||
      normalizedStatus.includes('aprobado')
    ) {
      return StudentDocumentStatus.Verified;
    }

    if (
      normalizedStatus.includes('rejected') ||
      normalizedStatus.includes('rechazado') ||
      normalizedStatus.includes('denied')
    ) {
      return StudentDocumentStatus.Rejected;
    }

    if (
      normalizedStatus.includes('review') ||
      normalizedStatus.includes('revision') ||
      normalizedStatus.includes('submitted') ||
      normalizedStatus.includes('uploaded') ||
      normalizedStatus.includes('entregado') ||
      normalizedStatus.includes('cargado')
    ) {
      return StudentDocumentStatus.Submitted;
    }

    return hasUploadedFile || this.stringValue(record, ['fileName', 'filename', 'fileUrl', 'url'])
      ? StudentDocumentStatus.Submitted
      : StudentDocumentStatus.Pending;
  }

  private toExtraActivity(value: unknown, index: number): ExtraActivityOffering {
    const record = this.unwrapRecord(value);

    return {
      publicId: this.stringValue(record, ['publicId', 'id']) || `O-${index + 1}`,
      name: this.stringValue(record, ['name', 'title']) || `Programa ${index + 1}`,
      type:
        this.stringValue(record, ['activityTypeName', 'type', 'activityType', 'category']) ||
        'course',
      modality: this.stringValue(record, ['modality', 'modalidad']) || 'En linea',
      startDate: this.dateValue(record, ['startDate', 'startAt', 'startsAt', 'date']),
      cost: this.numberValue(record, ['conceptCost']),
      totalHours: this.numberValue(record, ['totalHours']),
    };
  }

  private toExtraEnrollment(value: unknown, index: number): ExtraEnrollmentCourse {
    const record = this.unwrapRecord(value);
    const activity = this.recordValue(record, ['extraActivity', 'activity']);

    return {
      publicId: this.stringValue(record, ['publicId', 'id']) || `EC-${index + 1}`,
      name:
        this.stringValue(record, ['program', 'programName', 'activityName', 'name', 'title']) ||
        this.stringValue(activity, ['name', 'title']) ||
        `Programa ${index + 1}`,
      type: this.toExtraActivityType(
        this.stringValue(record, ['type', 'activityType']) ||
          this.stringValue(activity, ['type', 'activityType', 'category']),
      ),
      startDate:
        this.dateValue(record, ['startDate', 'startAt', 'startsAt']) ||
        this.dateValue(activity, ['startDate', 'startAt', 'startsAt']),
      endDate:
        this.dateValue(record, ['endDate', 'endAt', 'endsAt']) ||
        this.dateValue(activity, ['endDate', 'endAt', 'endsAt']),
      status: this.toExtraEnrollmentStatus(this.stringValue(record, ['status'])),
      grade: this.numberValue(record, ['grade', 'finalGrade', 'score']),
      certificateUrl: this.stringValue(record, ['certificateUrl', 'diplomaUrl']),
    };
  }

  private toKardex(value: unknown): StudentKardex {
    const record = this.unwrapRecord(value);
    const subjects = this.kardexSubjects(value).map((item, index) =>
      this.toAcademicSubject(item, index),
    );
    const semesters = this.toSemesters(subjects);
    const approvedSubjects =
      this.numberValue(record, [
        'approvedSubjects',
        'approvedCourses',
        'passedSubjects',
        'subjectsPassed',
      ]) ?? subjects.filter((subject) => subject.status === AcademicSubjectStatus.Approved).length;
    const approvedCredits =
      this.numberValue(record, ['approvedCredits', 'earnedCredits', 'creditsEarned']) ??
      subjects
        .filter((subject) => subject.status === AcademicSubjectStatus.Approved)
        .reduce((total, subject) => total + (subject.credits ?? 0), 0);
    const totalCredits =
      this.numberValue(record, [
        'totalCredits',
        'creditsTotal',
        'studyPlanCredits',
        'totalPlanCredits',
      ]) ?? subjects.reduce((total, subject) => total + (subject.credits ?? 0), 0);

    return {
      folio: this.stringValue(record, ['folio', 'reference']) || 'Sin folio',
      studentName: this.stringValue(record, ['studentName', 'fullName', 'name']) || 'Alumno',
      enrollmentCode:
        this.stringValue(record, [
          'enrollmentCode',
          'registrationNumber',
          'registration_number',
          'matricula',
          'studentNumber',
        ]) || 'Sin matricula',
      program:
        this.stringValue(record, ['program', 'programName', 'studyPlanName']) ||
        this.stringValue(this.recordValue(record, ['student', 'studyPlan']), ['name']) ||
        'Programa academico',
      plan:
        this.stringValue(record, ['plan', 'studyPlanVersion', 'planName', 'studyPlanName']) ||
        this.stringValue(this.recordValue(record, ['studyPlan', 'student']), ['name']) ||
        '',
      status: this.stringValue(record, ['status', 'studentStatus']) || 'Regular',
      average: this.kardexAverage(record, subjects),
      approvedSubjects,
      approvedCredits,
      totalCredits,
      semesters,
    };
  }

  private toAcademicSubject(value: unknown, index: number): AcademicSubject {
    const record = this.unwrapRecord(value);
    const subject = this.recordValue(record, ['subject', 'course', 'matter']);
    const period = this.recordValue(record, ['period', 'semester', 'term']);
    const rawStatus = this.stringValue(record, ['status', 'academicStatus']);

    return {
      publicId: this.stringValue(record, ['publicId', 'id']) || `AR-${index + 1}`,
      code:
        this.stringValue(record, ['code', 'subjectCode', 'courseCode']) ||
        this.stringValue(subject, ['code', 'clave']) ||
        `MAT-${index + 1}`,
      name:
        this.stringValue(record, ['name', 'subjectName', 'courseName']) ||
        this.stringValue(subject, ['name', 'title']) ||
        'Materia',
      grade: this.gradeValue(record, ['grade', 'finalGrade', 'score']),
      status: this.toAcademicStatus(rawStatus, this.gradeValue(record, ['grade', 'finalGrade'])),
      gradeSheetFolio: this.stringValue(record, ['gradeSheetFolio', 'folioNumber']) || null,
      cycleNumber: this.numberValue(record, ['cycleNumber', 'semesterNumber']) ?? 1,
      period:
        this.stringValue(record, ['periodName', 'semesterName', 'termName']) ||
        this.stringValue(period, ['name', 'title']) ||
        'Periodo sin asignar',
      credits:
        this.numberValue(record, ['credits', 'creditHours']) ??
        this.numberValue(subject, ['credits']),
      auditedAt: this.dateValue(record, ['auditedAt', 'updatedAt', 'gradedAt']),
    };
  }

  private toStudentAcademicRecord(value: unknown, index: number): StudentAcademicRecord {
    const record = this.unwrapRecord(value);
    const subject = this.recordValue(record, ['subject', 'course', 'matter']);
    const period = this.recordValue(record, ['period', 'semester', 'term']);
    const group = this.recordValue(record, ['group', 'cohort']);

    return {
      publicId:
        this.stringValue(record, ['publicId', 'academicRecordPublicId', 'id']) || `AR-${index + 1}`,
      periodPublicId:
        this.stringValue(record, ['periodPublicId', 'periodId']) ||
        this.stringValue(period, ['publicId', 'id']),
      subjectName:
        this.stringValue(record, ['subjectName', 'name', 'courseName']) ||
        this.stringValue(subject, ['name', 'title']) ||
        'Materia',
      groupCohortName:
        this.stringValue(record, ['groupCohortName', 'groupName', 'cohortName']) ||
        this.stringValue(group, ['cohortName', 'name', 'title']) ||
        '—',
      periodName:
        this.stringValue(record, ['periodName', 'semesterName', 'termName']) ||
        this.stringValue(period, ['name', 'title']) ||
        '—',
      professorFullName:
        this.stringValue(record, ['professorFullName', 'teacherFullName', 'instructorFullName']) ||
        '—',
      grade: this.nullableGradeValue(record, ['grade', 'finalGrade', 'score']),
      status: this.stringValue(record, ['status', 'academicStatus']),
      gradePartials: this.arrayValue(record, ['gradePartials']).map((partial, partialIndex) =>
        this.toGradePartial(partial, partialIndex),
      ),
    };
  }

  private toGradePartial(value: unknown, index: number): GradePartial {
    const record = this.unwrapRecord(value);

    return {
      publicId: this.stringValue(record, ['publicId', 'id']) || `GP-${index + 1}`,
      name: this.stringValue(record, ['name', 'title', 'description']) || `Parcial ${index + 1}`,
      type: this.stringValue(record, ['type', 'evaluationType']) || 'Evaluacion',
      score: this.numberValue(record, ['score', 'grade']),
      weight: this.numberValue(record, ['weight', 'percentage']),
      date: this.dateValue(record, ['date', 'createdAt', 'evaluatedAt']),
    };
  }

  private toMoodleAccount(value: unknown): MoodleAccount {
    const record = this.unwrapRecord(value);
    const cohort = this.stringValue(record, ['cohort', 'cohorte', 'group']);
    const phone = this.stringValue(record, ['phone', 'telefono', 'whatsApp']);

    return {
      publicId: this.stringValue(record, ['publicId', 'id']),
      firstName: this.stringValue(record, ['firstName', 'name', 'nombre']),
      lastName: this.stringValue(record, ['lastName', 'lastNames', 'apellido']),
      username: this.stringValue(record, ['username', 'userName', 'user']),
      password: this.stringValue(record, ['password', 'pass']),
      email: this.stringValue(record, ['email']),
      cohort: cohort || null,
      phone: phone || null,
      curp: this.stringValue(record, ['curp', 'CURP']),
    };
  }

  private toPermissions(value: unknown): StudentPermissions {
    const record = this.unwrapRecord(value);

    return {
      userType: this.stringValue(record, ['userType', 'type']),
      roles: this.stringArrayValue(record, ['roles']),
      permissions: this.stringArrayValue(record, ['permissions']),
    };
  }

  private stringArrayValue(record: ApiRecord, keys: string[]): string[] {
    const value = keys.map((key) => record[key]).find(Array.isArray);

    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string')
      : [];
  }

  private toArray(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      return value;
    }

    if (!this.isRecord(value)) {
      return [];
    }

    const listKeys = [
      'items',
      'data',
      'results',
      'students',
      'users',
      'payments',
      'enrollments',
      'activities',
      'subjects',
      'schedules',
      'groupSchedules',
      'records',
      'academicRecords',
      'partials',
      'documents',
      'studentDocuments',
      'requiredDocuments',
    ];
    const directList = listKeys.map((key) => value[key]).find(Array.isArray);

    if (Array.isArray(directList)) {
      return directList;
    }

    const record = this.unwrapRecord(value);
    const nestedList = listKeys.map((key) => record[key]).find(Array.isArray);

    return Array.isArray(nestedList) ? nestedList : [];
  }

  private arrayValue(record: ApiRecord, keys: string[]): unknown[] {
    const value = keys.map((key) => record[key]).find(Array.isArray);
    return Array.isArray(value) ? value : [];
  }

  private kardexSubjects(value: unknown): unknown[] {
    const direct = this.toArray(value);

    if (direct.length > 0) {
      return direct;
    }

    const record = this.unwrapRecord(value);
    const semesters = ['semesters', 'periods', 'terms']
      .map((key) => record[key])
      .find(Array.isArray);

    if (!Array.isArray(semesters)) {
      return [];
    }

    return semesters.flatMap((semester) => {
      if (!this.isRecord(semester)) {
        return [];
      }

      const periodName = this.stringValue(semester, ['title', 'name', 'periodName']);
      const cycleNumber = this.numberValue(semester, ['cycleNumber', 'semesterNumber']) ?? 1;
      const subjects = ['subjects', 'courses', 'records', 'academicRecords']
        .map((key) => semester[key])
        .find(Array.isArray);

      return Array.isArray(subjects)
        ? subjects.map((subject) =>
            this.isRecord(subject) ? { ...subject, periodName, cycleNumber } : subject,
          )
        : [];
    });
  }

  private toSemesters(subjects: AcademicSubject[]): AcademicSemester[] {
    const groups = new Map<number, AcademicSubject[]>();

    for (const subject of subjects) {
      groups.set(subject.cycleNumber, [...(groups.get(subject.cycleNumber) ?? []), subject]);
    }

    return [...groups.entries()]
      .sort(([first], [second]) => first - second)
      .map(([cycleNumber, group]) => ({
        title: `Cuatrimestre ${cycleNumber}`,
        status: group.some((subject) => subject.status === AcademicSubjectStatus.InProgress)
          ? AcademicSubjectStatus.InProgress
          : '',
        subjects: group,
      }));
  }

  private kardexAverage(record: ApiRecord, subjects: AcademicSubject[]): string {
    const explicit = this.numberValue(record, [
      'overallAverage',
      'average',
      'gpa',
      'generalAverage',
    ]);

    if (explicit !== null) {
      return explicit.toFixed(1);
    }

    const grades = subjects
      .map((subject) => Number(subject.grade))
      .filter((grade) => Number.isFinite(grade));

    if (grades.length === 0) {
      return '--';
    }

    return (grades.reduce((total, grade) => total + grade, 0) / grades.length).toFixed(1);
  }

  private gradeValue(record: ApiRecord, keys: string[]): string {
    const number = this.numberValue(record, keys);

    if (number !== null) {
      return String(number);
    }

    return this.stringValue(record, keys) || '--';
  }

  private nullableGradeValue(record: ApiRecord, keys: string[]): string | null {
    const value = keys.map((key) => record[key]).find((item) => item !== undefined);

    if (value === null) {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? String(value) : null;
    }

    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private toAcademicStatus(value: string, grade: string): AcademicSubject['status'] {
    const normalized = value.toLowerCase();

    if (
      normalized.includes('passed') ||
      normalized.includes('approved') ||
      normalized.includes('aprob')
    ) {
      return AcademicSubjectStatus.Approved;
    }

    if (
      normalized.includes('failed') ||
      normalized.includes('reprob') ||
      normalized.includes('withdrawn')
    ) {
      return AcademicSubjectStatus.Failed;
    }

    const numericGrade = Number(grade);

    if (Number.isFinite(numericGrade)) {
      return numericGrade >= 6 ? AcademicSubjectStatus.Approved : AcademicSubjectStatus.Failed;
    }

    return AcademicSubjectStatus.InProgress;
  }

  private toExtraActivityType(value: string): ExtraEnrollmentCourse['type'] {
    const normalized = value.toLowerCase();

    if (normalized.includes('diplom')) {
      return 'diploma';
    }

    if (normalized.includes('taller')) {
      return 'workshop';
    }

    return 'course';
  }

  private toExtraEnrollmentStatus(value: string): ExtraEnrollmentCourse['status'] {
    const normalized = value.toLowerCase();

    if (
      normalized.includes('completed') ||
      normalized.includes('concluido') ||
      normalized.includes('finished')
    ) {
      return ExtraEnrollmentStatus.Completed;
    }

    if (
      normalized.includes('active') ||
      normalized.includes('progress') ||
      normalized.includes('cursando')
    ) {
      return ExtraEnrollmentStatus.InProgress;
    }

    return ExtraEnrollmentStatus.Pending;
  }

  private toCount(value: unknown): number {
    if (typeof value === 'number') {
      return value;
    }

    const record = this.unwrapRecord(value);

    return this.numberValue(record, ['count', 'unreadCount', 'total']) ?? 0;
  }

  private unwrapRecord(value: unknown): ApiRecord {
    if (!this.isRecord(value)) {
      return {};
    }

    const nested = ['data', 'item', 'result', 'profile']
      .map((key) => value[key])
      .find((item) => this.isRecord(item));

    if (this.isRecord(nested)) {
      return nested;
    }

    if (!this.hasProfileFields(value) && this.isRecord(value['user'])) {
      return value['user'];
    }

    return value;
  }

  private normalizeGradeCorrectionRequests(
    requests: GradeCorrectionRequest[],
  ): GradeCorrectionRequest[] {
    const knownStates: GradeCorrectionRequestState[] = [
      'submitted',
      'under_review',
      'approved',
      'rejected',
      'registered',
    ];

    const toKnownState = (value: unknown): GradeCorrectionRequestState | undefined => {
      if (typeof value !== 'string') {
        return undefined;
      }

      const key = value.toLowerCase().replace(/[_\-\s]/g, '');

      return knownStates.find((known) => known.replace('_', '') === key);
    };

    return requests.map((request) => {
      const response = request as GradeCorrectionRequest & { status?: unknown };

      return {
        ...request,
        state: toKnownState(request.state) ?? toKnownState(response.status) ?? 'submitted',
      };
    });
  }

  private hasProfileFields(record: ApiRecord): boolean {
    return [
      'publicId',
      'id',
      'email',
      'firstName',
      'lastName',
      'fullName',
      'studyPlan',
      'studyPlanName',
      'program',
      'programName',
      'currentSemester',
    ].some((key) => key in record);
  }

  private recordValue(record: ApiRecord, keys: string[]): ApiRecord {
    const value = keys.map((key) => record[key]).find((item) => this.isRecord(item));
    return this.isRecord(value) ? value : {};
  }

  private studentPublicId(record: ApiRecord): string {
    const direct = this.stringValue(record, [
      'studentPublicId',
      'studentId',
      'student_public_id',
      'student_id',
    ]);

    if (direct) {
      return direct;
    }

    const nested = this.stringValue(
      this.recordValue(record, ['student', 'studentProfile', 'alumno']),
      ['publicId', 'id'],
    );

    if (nested) {
      return nested;
    }

    return this.studentPublicIdFromNested(record, new Set<ApiRecord>());
  }

  private studentPublicIdFromNested(record: ApiRecord, visited: Set<ApiRecord>): string {
    if (visited.has(record)) {
      return '';
    }

    visited.add(record);

    for (const [key, value] of Object.entries(record)) {
      if (!this.isRecord(value)) {
        continue;
      }

      const normalizedKey = key.toLowerCase();

      if (normalizedKey.includes('student') || normalizedKey.includes('alumno')) {
        const id = this.stringValue(value, ['studentPublicId', 'studentId', 'publicId', 'id']);

        if (id) {
          return id;
        }
      }

      const nested = this.studentPublicIdFromNested(value, visited);

      if (nested) {
        return nested;
      }
    }

    return '';
  }

  private studyPlanPublicId(record: ApiRecord): string {
    const direct = this.stringValue(record, [
      'studyPlanPublicId',
      'studyPlanId',
      'study_plan_public_id',
      'study_plan_id',
    ]);

    if (direct) {
      return direct;
    }

    const nested = this.stringValue(this.recordValue(record, ['studyPlan', 'program']), [
      'publicId',
      'id',
    ]);

    if (nested) {
      return nested;
    }

    return this.studyPlanPublicIdFromNested(record, new Set<ApiRecord>());
  }

  private studyPlanPublicIdFromNested(record: ApiRecord, visited: Set<ApiRecord>): string {
    if (visited.has(record)) {
      return '';
    }

    visited.add(record);

    const direct =
      this.stringValue(record, [
        'studyPlanPublicId',
        'studyPlanId',
        'study_plan_public_id',
        'study_plan_id',
      ]) ||
      this.stringValue(this.recordValue(record, ['studyPlan', 'program']), ['publicId', 'id']);

    if (direct) {
      return direct;
    }

    for (const value of Object.values(record)) {
      if (this.isRecord(value)) {
        const nested = this.studyPlanPublicIdFromNested(value, visited);

        if (nested) {
          return nested;
        }
      }
    }

    return '';
  }

  private studyPlanName(record: ApiRecord): string {
    const direct =
      this.stringValue(record, [
        'studyPlanName',
        'programName',
        'planName',
        'academicProgramName',
        'study_plan_name',
        'program_name',
        'plan_name',
      ]) ||
      this.stringValue(
        this.recordValue(record, ['studyPlan', 'program', 'plan', 'academicProgram']),
        [
          'studyPlanName',
          'programName',
          'planName',
          'academicProgramName',
          'name',
          'title',
          'description',
        ],
      );

    if (direct) {
      return direct;
    }

    return this.studyPlanNameFromNested(record, new Set<ApiRecord>());
  }

  private studyPlanNameFromNested(record: ApiRecord, visited: Set<ApiRecord>): string {
    if (visited.has(record)) {
      return '';
    }

    visited.add(record);

    const direct =
      this.stringValue(record, [
        'studyPlanName',
        'programName',
        'planName',
        'academicProgramName',
        'study_plan_name',
        'program_name',
        'plan_name',
      ]) ||
      this.stringValue(
        this.recordValue(record, ['studyPlan', 'program', 'plan', 'academicProgram']),
        [
          'studyPlanName',
          'programName',
          'planName',
          'academicProgramName',
          'name',
          'title',
          'description',
        ],
      );

    if (direct) {
      return direct;
    }

    for (const value of Object.values(record)) {
      if (this.isRecord(value)) {
        const nested = this.studyPlanNameFromNested(value, visited);

        if (nested) {
          return nested;
        }
      }
    }

    return '';
  }

  private toPublicStudyPlan(value: unknown): PublicStudyPlan {
    const record = this.unwrapRecord(value);

    return {
      publicId: this.stringValue(record, ['publicId', 'id']),
      name: this.studyPlanResourceName(record),
      degreeLevel: this.stringValue(record, ['degreeLevel', 'level']),
    };
  }

  private studyPlanResourceName(record: ApiRecord): string {
    const direct =
      this.stringValue(record, [
        'studyPlanName',
        'programName',
        'planName',
        'academicProgramName',
        'study_plan_name',
        'program_name',
        'plan_name',
        'name',
        'title',
        'description',
      ]) ||
      this.stringValue(
        this.recordValue(record, ['studyPlan', 'program', 'plan', 'academicProgram']),
        [
          'studyPlanName',
          'programName',
          'planName',
          'academicProgramName',
          'name',
          'title',
          'description',
        ],
      );

    if (direct) {
      return direct;
    }

    return this.studyPlanResourceNameFromNested(record, new Set<ApiRecord>());
  }

  private studyPlanResourceNameFromNested(record: ApiRecord, visited: Set<ApiRecord>): string {
    if (visited.has(record)) {
      return '';
    }

    visited.add(record);

    for (const [key, value] of Object.entries(record)) {
      if (!this.isRecord(value)) {
        continue;
      }

      const normalizedKey = key.toLowerCase();

      if (
        normalizedKey.includes('studyplan') ||
        normalizedKey.includes('program') ||
        normalizedKey.includes('plan')
      ) {
        const name = this.stringValue(value, [
          'studyPlanName',
          'programName',
          'planName',
          'academicProgramName',
          'name',
          'title',
          'description',
        ]);

        if (name) {
          return name;
        }
      }

      const nested = this.studyPlanResourceNameFromNested(value, visited);

      if (nested) {
        return nested;
      }
    }

    return '';
  }

  private stringValue(record: ApiRecord, keys: string[]): string {
    const value = keys
      .map((key) => record[key])
      .find((item) => typeof item === 'string' || typeof item === 'number');

    if (typeof value === 'number') {
      return String(value);
    }

    return typeof value === 'string' ? value.trim() : '';
  }

  private numberValue(record: ApiRecord, keys: string[]): number | null {
    const value = keys
      .map((key) => record[key])
      .find((item) => typeof item === 'number' || typeof item === 'string');

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string') {
      const normalized = value.replace(/[$,\s]/g, '').trim();

      if (!normalized) {
        return null;
      }

      const parsed = Number(normalized);

      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private dateValue(record: ApiRecord, keys: string[]): string | null {
    const value = keys.map((key) => record[key]).find((item) => typeof item === 'string');
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
  }

  private studentDocumentFileUrl(record: ApiRecord, fileRecord: ApiRecord): string {
    const directKeys = [
      'fileAccessUrl',
      'fileUrl',
      'url',
      'documentUrl',
      'downloadUrl',
      'previewUrl',
      'signedUrl',
      'uploadedFileUrl',
      'attachmentUrl',
      'resourceUrl',
      'filePath',
      'path',
      'documentPath',
      'storagePath',
      'objectKey',
      'blobName',
    ];

    return (
      this.stringValue(record, directKeys) ||
      this.stringValue(fileRecord, directKeys) ||
      this.fileUrlFromNested(record, new Set<ApiRecord>())
    );
  }

  private fileUrlFromNested(record: ApiRecord, visited: Set<ApiRecord>): string {
    if (visited.has(record)) {
      return '';
    }

    visited.add(record);

    for (const [key, value] of Object.entries(record)) {
      const normalizedKey = this.normalizeToken(key);

      if (
        (typeof value === 'string' || typeof value === 'number') &&
        this.looksLikeFileReference(normalizedKey, String(value))
      ) {
        return String(value).trim();
      }

      if (this.isRecord(value)) {
        const nested = this.fileUrlFromNested(value, visited);

        if (nested) {
          return nested;
        }
      }
    }

    return '';
  }

  private looksLikeFileReference(key: string, value: string): boolean {
    const normalizedValue = value.trim().toLowerCase();

    if (!normalizedValue) {
      return false;
    }

    const keyLooksUseful =
      key.includes('url') ||
      key.includes('path') ||
      key.includes('file') ||
      key.includes('archivo') ||
      key.includes('document') ||
      key.includes('storage') ||
      key.includes('object') ||
      key.includes('blob');
    const valueLooksUseful =
      /^(https?:|blob:|data:)/i.test(normalizedValue) ||
      normalizedValue.startsWith('/') ||
      /\.(pdf|jpe?g|png|gif|webp)(\?|#|$)/i.test(normalizedValue);

    return keyLooksUseful && valueLooksUseful;
  }

  private toResourceUrl(value: string): string {
    if (!value) {
      return '';
    }

    if (/^(https?:|blob:|data:)/i.test(value)) {
      return value;
    }

    const apiUrl = this.apiUrl.replace(/\/$/, '');
    const resourcePath = value.startsWith('/') ? value : `/${value}`;

    return `${apiUrl}${resourcePath}`;
  }

  private normalizeToken(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  private isRecord(value: unknown): value is ApiRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
